import { z } from "zod";
import type { Priority } from "./enums.js";
import type { ObligationStatus } from "./obligations.js";
import { priorityOf } from "./priority.js";
import type { ObligationBasis, ObligationKind } from "./library.js";
import type { RecordResult } from "./obligations.js";
import type { MaterialUnit } from "./materials.js";
import type { BuyoutInfoDto } from "./contracts.js";
import { answersSchema, type Answers } from "./questions.js";

/**
 * Meldingen van buiten (docs/MELDINGEN.md, issue #136): een melding komt eerst in de inbox *Te beoordelen*
 * en wordt pas daarna een bevinding, of gekoppeld aan een bevinding die er al is, of afgewezen.
 */

/**
 * Waar de melding vandaan komt. *Keuring* en *Onderhoud*: een registratie met opmerkingen of afgekeurd
 * (issue #151); keuring staat ook voor inspectie en controle. Later: gebouwlink, meldpagina, e-mail.
 */
export const INTAKE_CHANNELS = ["asset_qr", "app", "keuring", "onderhoud", "dossier", "voorraad", "contract"] as const;
export type IntakeChannel = (typeof INTAKE_CHANNELS)[number];
export const INTAKE_CHANNEL_LABELS: Record<IntakeChannel, string> = {
  asset_qr: "Asset-QR",
  app: "App",
  keuring: "Keuring",
  onderhoud: "Onderhoud",
  dossier: "Dossier",
  voorraad: "Voorraad",
  contract: "Contract",
};
/** Kanalen uit een keuring of onderhoud (geen melder van buiten, geen vragen beantwoord). */
export const isObligationChannel = (c: IntakeChannel) => c === "keuring" || c === "onderhoud";
/** Uitslagen van een keuring of onderhoud die in de Inbox komen in plaats van direct een bevinding te worden. */
export const INTAKE_RECORD_RESULTS: readonly RecordResult[] = ["afgekeurd", "met_opmerkingen"];

export const INTAKE_STATUSES = ["new", "converted", "linked", "rejected", "handled"] as const;
export type IntakeStatus = (typeof INTAKE_STATUSES)[number];
export const INTAKE_STATUS_LABELS: Record<IntakeStatus, string> = {
  new: "Te beoordelen",
  converted: "Bevinding gemaakt",
  linked: "Gekoppeld",
  rejected: "Afgewezen",
  handled: "Afgehandeld",
};

/** Wie meldt. Bij het kanaal App is dat de ingelogde gebruiker. */
export const INTAKE_REPORTERS = ["bewoner", "leverancier", "gebruiker"] as const;
export type IntakeReporter = (typeof INTAKE_REPORTERS)[number];
export const INTAKE_REPORTER_LABELS: Record<IntakeReporter, string> = {
  bewoner: "Bewoner of gebruiker",
  leverancier: "Leverancier",
  gebruiker: "Ingelogde gebruiker",
};

type Opt = { k: string; l: string; h?: string };

/** De vragen op het meldscherm, in gewone taal. */
export const INTAKE_Q = {
  what: {
    q: "Wat is er aan de hand?",
    opts: [
      { k: "lekt", l: "Lekt" },
      { k: "werkt_niet", l: "Werkt niet" },
      { k: "kapot", l: "Kapot of beschadigd" },
      { k: "onveilig", l: "Onveilig" },
      { k: "anders", l: "Anders" },
    ],
  },
  danger: {
    q: "Is het gevaarlijk?",
    opts: [
      { k: "ja", l: "Ja" },
      { k: "nee", l: "Nee" },
      { k: "weet_niet", l: "Weet ik niet" },
    ],
  },
  usable: {
    q: "Kan het nog gebruikt worden?",
    opts: [
      { k: "ja", l: "Ja" },
      { k: "beperkt", l: "Beperkt" },
      { k: "nee", l: "Nee" },
    ],
  },
  since: {
    q: "Sinds wanneer?",
    opts: [
      { k: "vandaag", l: "Vandaag" },
      { k: "week", l: "Deze week" },
      { k: "langer", l: "Langer" },
    ],
  },
} as const satisfies Record<string, { q: string; opts: readonly Opt[] }>;

export type IntakeQuestion = keyof typeof INTAKE_Q;
type Keys<T extends IntakeQuestion> = (typeof INTAKE_Q)[T]["opts"][number]["k"];
const keysOf = <T extends IntakeQuestion>(q: T) =>
  INTAKE_Q[q].opts.map((o) => o.k) as unknown as [Keys<T>, ...Keys<T>[]];
export const intakeLabel = (q: IntakeQuestion, k: string): string =>
  (INTAKE_Q[q].opts as readonly Opt[]).find((o) => o.k === k)?.l ?? "—";

export const INTAKE_REJECT_REASONS = ["geen_probleem", "spam", "niet_ons_gebouw", "anders"] as const;
export type IntakeRejectReason = (typeof INTAKE_REJECT_REASONS)[number];
export const INTAKE_REJECT_LABELS: Record<IntakeRejectReason, string> = {
  geen_probleem: "Geen probleem",
  spam: "Spam of onzin",
  niet_ons_gebouw: "Niet ons gebouw of onze asset",
  anders: "Anders",
};

/** Foto's bij een melding: minstens één, tenzij "Foto maken lukt niet". */
export const INTAKE_MAX_PHOTOS = 4;
export const INTAKE_PHOTO_MAX_BYTES = 10 * 1024 * 1024;

const text = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((s) => s || null)
    .nullish()
    .transform((s) => s ?? null);

/** Het meldscherm (zonder inloggen of in de app). Foto's gaan als bestanden mee in hetzelfde formulier. */
export const intakeFormSchema = z
  .object({
    what: z.enum(keysOf("what"), { message: "Kies wat er aan de hand is" }),
    danger: z.enum(keysOf("danger"), { message: "Geef aan of het gevaarlijk is" }),
    usable: z.enum(keysOf("usable"), { message: "Geef aan of het nog gebruikt kan worden" }),
    since: z.enum(keysOf("since"), { message: "Geef aan sinds wanneer" }),
    description: text(2000),
    noPhoto: z
      .union([z.boolean(), z.enum(["true", "false", "1", "0", "on"])])
      .optional()
      .transform((v) => v === true || v === "true" || v === "1" || v === "on"),
    reporter: z.enum(["bewoner", "leverancier"]).default("bewoner"),
    name: text(120),
    email: z
      .string()
      .trim()
      .max(200)
      .transform((s) => s.toLowerCase())
      .pipe(z.union([z.literal(""), z.email({ message: "Vul een geldig e-mailadres in" })]))
      .optional()
      .transform((s) => s || null),
    phone: text(40),
  })
  .refine((f) => f.description || f.what !== "anders", {
    message: "Beschrijf kort wat er aan de hand is",
    path: ["description"],
  });
export type IntakeForm = z.infer<typeof intakeFormSchema>;

/** Antwoorden van de melder (opgeslagen bij de melding). */
export interface IntakeAnswers {
  what: Keys<"what">;
  danger: Keys<"danger">;
  usable: Keys<"usable">;
  since: Keys<"since">;
}

/**
 * Vertaling naar de vragenlijst van een bevinding: zo berekent de app een voorgestelde prioriteit, die de
 * servicedesk bij het omzetten nog kan aanpassen.
 */
export function intakeToIssueAnswers(a: IntakeAnswers): Answers {
  return answersSchema.parse({
    type: a.what === "lekt" ? "sanitair" : "overig",
    danger: a.danger === "ja" ? "ja" : a.danger === "weet_niet" ? "mogelijk" : "nee",
    impact: a.usable === "nee" ? "deel" : a.usable === "beperkt" ? "hinder" : "geen",
    worsen: a.what === "lekt" ? "snel" : a.what === "onveilig" ? "langzaam" : "nee",
    origin: "melding",
  });
}
export const intakePriority = (a: IntakeAnswers): Priority => priorityOf(intakeToIssueAnswers(a));

/** Omzetten in een bevinding: titel en vragenlijst, voorgevuld vanuit de melding. */
export const intakeConvertSchema = z.object({
  /** Client-UUID van de nieuwe bevinding (idempotent). */
  issueId: z.uuid(),
  title: z.string().trim().min(1, "Vul een titel in").max(200),
  description: text(4000),
  answers: answersSchema,
  /** Gebreken (submeldingen) die in deze bevinding komen; leeg = alle nog te beoordelen gebreken. */
  itemIds: z.array(z.uuid()).max(50).optional(),
});
export const intakeLinkSchema = z.object({ issueId: z.uuid(), itemIds: z.array(z.uuid()).max(50).optional() });
export const intakeRejectSchema = z.object({
  reason: z.enum(INTAKE_REJECT_REASONS),
  note: text(1000),
  itemIds: z.array(z.uuid()).max(50).optional(),
});
/** Max. aantal gebreken per keuring of onderhoud (submeldingen in de Inbox). */
export const INTAKE_MAX_ITEMS = 20;

/** Eén gebrek uit een keuring of onderhoud (submelding). */
export interface IntakeItemDto {
  id: string;
  text: string;
  status: IntakeStatus;
  issue: { id: string; code: string; title: string } | null;
  rejectReason: IntakeRejectReason | null;
}
export const intakeListQuery = z.object({
  status: z.enum([...INTAKE_STATUSES, "alle"]).default("new"),
  buildingId: z.uuid().optional(),
});
export const intakeSettingsSchema = z.object({ assetQr: z.boolean() });

export interface IntakeReportDto {
  id: string;
  /** Afkoop storingen: de storingspost van een lopend contract die de asset dekt (module Contracten). */
  buyout?: BuyoutInfoDto | null;
  code: string;
  status: IntakeStatus;
  channel: IntakeChannel;
  /** Kop in de inbox, zonder de asset (bijv. "Lekt" of "Keuring afgekeurd: NEN 3140"). */
  title: string;
  /** Voorstel voor *Bevinding maken*. */
  draft: { title: string; description: string | null; answers: Answers };
  /** Bij kanaal Keuring of Onderhoud: de registratie waar de melding uit komt. */
  obligation: {
    id: string;
    recordId: string;
    name: string;
    kind: ObligationKind;
    basis: ObligationBasis;
    result: RecordResult;
    performedOn: string;
    performedByCompany: string | null;
    certificateFileId: string | null;
  } | null;
  /** Bij kanaal Dossier: het document dat verloopt of verlopen is. */
  document: {
    id: string;
    title: string;
    typeName: string;
    validUntil: string | null;
    version: number;
    fileId: string;
  } | null;
  /** Bij kanaal Contract: het contract waar het signaal over gaat (opzeggen nadert, prijs niet bijgewerkt). */
  contract?: { id: string; name: string; signal: "notice" | "end" | "price" | null } | null;
  /** Bij kanaal Voorraad: het artikel onder het minimum, met de huidige stand in het gebouw. */
  material: {
    id: string;
    name: string;
    articleNo: string | null;
    unit: MaterialUnit;
    quantity: number;
    minQuantity: number | null;
    location: string | null;
  } | null;
  building: { id: string; code: string; name: string };
  asset: { id: string; code: string; name: string } | null;
  answers: IntakeAnswers;
  description: string | null;
  noPhoto: boolean;
  photos: string[];
  suggestedPriority: Priority;
  reporter: { kind: IntakeReporter; name: string | null; email: string | null; phone: string | null };
  /** Bevinding na omzetten of koppelen. */
  issue: { id: string; code: string; title: string } | null;
  /** Gebreken als losse regels (keuring of onderhoud); leeg bij een gewone melding. */
  items: IntakeItemDto[];
  rejectReason: IntakeRejectReason | null;
  rejectNote: string | null;
  handledBy: { id: string; name: string | null; isClient: boolean } | null;
  handledAt: string | null;
  createdAt: string;
}
export interface IntakeListResponse {
  items: IntakeReportDto[];
  /** Aantal te beoordelen (zichtbare gebouwen). */
  open: number;
}

/** Wat iemand zonder inloggen ziet na het scannen van een asset-QR. */
export interface PublicAssetDto {
  asset: { code: string; name: string };
  building: { name: string; city: string | null };
  org: { name: string };
  /** Keuringen: alleen naam, status en volgende datum. */
  obligations: { name: string; status: ObligationStatus; nextDueOn: string | null }[];
  /** Staat "Melden via QR-code bij een asset" aan voor dit gebouw? */
  canReport: boolean;
}
